<script setup lang="ts">
import {ConsumerProduct, ConsumerProductIssue, SourceType} from "@/consumer/interfaces";
import ConsumerProductChip from "@/drug-interaction/components/header/ConsumerProductChip.vue";
import DosageService from "@/dosage/services/DosageService";
import {Pill} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Button} from "@/core/components/ui/button";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import {computed} from "vue";
import {consumerProductApi} from "@/container";
import ConsumerProductIssueRow from "./ConsumerProductIssueRow.vue";
import {Dialog, DialogContent, DialogTrigger} from "@/core/components/ui/dialog";

defineProps<{
    index: number;
}>();

const consumerProduct = defineModel<ConsumerProduct>("consumerProduct", {required: true});

const emits = defineEmits<{
    (e: 'openDosageDialog', product: ConsumerProduct): void;
}>();

/**
 * Computed source type with getter and setter to update the API on change.
 */
const source = computed({
    get: () => {
        return consumerProduct.value.source_type || 'unknown';
    },
    set: async (value) => {
        const formattedValue = value === 'unknown' ? null : value;
        const copy = consumerProduct.value.source_type;

        consumerProduct.value.source_type = formattedValue;

        try {
            await consumerProductApi().updateConsumerProduct(
                consumerProduct.value.id, {source_type: formattedValue}
            );
        } catch {
            consumerProduct.value.source_type = copy;
        }
    }
});

/**
 * Get an issue of the consumer product by type.
 */
const getIssue = (type: ConsumerProductIssue['type']): ConsumerProductIssue | undefined => {
    return consumerProduct.value.issues?.find(issue => issue.type === type);
};

/**
 * Update an issue of the consumer product by type.
 */
const setIssue = (type: ConsumerProductIssue['type'], updated: ConsumerProductIssue | undefined) => {
    const others = (consumerProduct.value.issues ?? []).filter(issue => issue.type !== type);
    consumerProduct.value.issues = updated ? [...others, updated] : others;
};
</script>

<template>
  <div class="bg-white rounded-lg p-4 flex gap-4 border-black border-[1px]">
    <div class="flex flex-col gap-4 flex-1">
      <div class="flex">
        <ConsumerProductChip :index="index" :product="consumerProduct.product" />
      </div>
      <div>
        <BaseSelect
          v-model:model-value="source"
          trigger-class="w-auto"
          placeholder="Sélectionner la provenance"
          :options="[
            {value: 'unknown', label: 'Inconnu'},
            {value: SourceType.PRESCRIPTION, label: 'Ordonnance'},
            {value: SourceType.SELF_MEDICATION, label: 'Automédication'},
          ]"
        />
      </div>
      <ConsumerProductIssueRow
        :consumer-product-id="consumerProduct.id"
        type="side_effect"
        label="Effets indésirables"
        :issue="getIssue('side_effect')"
        @update:issue="setIssue('side_effect', $event)"
      />
      <ConsumerProductIssueRow
        :consumer-product-id="consumerProduct.id"
        type="galenic"
        label="Problème avec la forme galénique"
        :issue="getIssue('galenic')"
        @update:issue="setIssue('galenic', $event)"
      />
      <div v-if="consumerProduct.product.latest_dosage" class="flex gap-1 items-center px-2">
        <Pill class="shrink-0 size-4 text-primary" />
        <p class="text-sm">
          {{ DosageService.humanizeDosage(consumerProduct.product.latest_dosage) }}
        </p>
        <Button variant="link" class="text-primary" size="sm" @click="emits('openDosageDialog' , consumerProduct)">
          Modifier
        </Button>
      </div>
      <div v-else>
        <BaseButton density="comfortable" primary @click="emits('openDosageDialog', consumerProduct)">
          Renseigner la posologie
        </BaseButton>
      </div>
    </div>
    <div class="flex flex-col gap-2 flex-1">
      <div v-if="consumerProduct.observance">
        <div
          class="px-2 leading-none py-2 rounded-full font-medium text-sm w-fit mb-2"
          :style="{backgroundColor: consumerProduct.observance.color}"
        >
          {{ consumerProduct.observance.title }}
        </div>
        <ul class="list-disc list-inside">
          <li v-for="reason in consumerProduct.observance.reasons" :key="reason">
            {{ reason }}
          </li>
        </ul>
      </div>
    </div>
    <div class="flex flex-1">
      <div v-if="consumerProduct.alerts?.length">
        <Dialog>
          <DialogTrigger>
            <div class="px-2 leading-none py-2 rounded-full font-medium text-sm w-fit mb-2 bg-red-100 text-red-800">
              {{ consumerProduct.alerts.length }} alerte{{ consumerProduct.alerts.length > 1 ? 's' : '' }}
            </div>
          </DialogTrigger>
          <DialogContent class="flex flex-col gap-2 max-h-[70vh] max-w-3xl overflow-y-auto">
            <ul class="flex flex-col gap-4">
              <li v-for="alert in consumerProduct.alerts" :key="alert.id" class="text-sm text-red-700 font-medium whitespace-pre-wrap">
                - {{ alert.content }}
              </li>
            </ul>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
